import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiUpload } from '@/services/api';
import { useTenantId } from './useTenant';

/** Matches core/import_engine.py's ENTITY_SCHEMAS.
 *
 * The middle four were added for P1-1: they are the entities that cost the
 * most to key in by hand on an installation day — roughly six actions per
 * driver, a new site most weeks, and a price per quarry per material, which
 * is what every revenue figure in the product is computed from.
 *
 * vehicle_gps_ping is a different shape of import (a telematics export, not
 * spreadsheet master data) but shares the same preview/commit/undo pipeline
 * — see core/gps_report.py for what it's for. */
export const IMPORT_ENTITIES = ['client', 'quarry', 'vehicle', 'driver', 'client_site', 'quarry_price',
  'service_record', 'vehicle_gps_ping'];

/** Which caches an import of each entity invalidates. A client import can
 *  change what the sites list shows (site_count), so it is listed too. */
const AFFECTED_KEYS = {
  client: ['clients', 'sites'],
  quarry: ['quarries'],
  vehicle: ['vehicles'],
  // A driver import also changes the fleet view (drivers carry a plate) and
  // the dashboard's headcount; a site import changes the sites list and the
  // clients list beside it (site_count). Without an entry here the list the
  // operator is looking at stays exactly as it was and the import appears
  // to have done nothing.
  driver: ['drivers', 'dashboard'],
  client_site: ['sites', 'clients'],
  quarry_price: ['quarry-prices', 'quarries', 'analytics'],
  // A service-history import is the whole point of V-11's due list: every
  // deadline is measured from the last recorded service, so importing the
  // history is what turns a fleet of "no baseline" rows into dates. Without
  // these keys the panel the operator is staring at keeps showing the empty
  // answer and the import looks like it did nothing.
  // 'service-records' since V-60: importing four years of history is the
  // commonest way this table is ever filled, and the panel that now shows
  // it sits on the same tab as the import button.
  service_record: ['service-due', 'service-on-time', 'service-records'],
  // useGpsReport() (core/gps_report.py) reads vehicle_gps_pings — without
  // this the modal a dispatcher just used to import the file keeps showing
  // the pre-import report until something else happens to refetch it.
  vehicle_gps_ping: ['gps-report'],
};

/**
 * V-65. DELETE /api/import/templates/<entity_type>/<template_id>.
 *
 * The route has existed and been tested since templates did, with no caller
 * anywhere. It matters because of how a template gets USED: the preview
 * auto-applies templates[0] — alphabetically first by name — whenever the
 * caller does not name one. So a template saved with a typo, or for a
 * supplier no longer used, does not merely sit there: it goes on being the
 * one applied to every file, for ever.
 */
export function useDeleteImportTemplate(entityType) {
  return useMutation({
    mutationFn: (templateId) =>
      api(`/api/import/templates/${entityType}/${templateId}`, { method: 'DELETE' }),
  });
}

function useImportInvalidation(entityType) {
  const queryClient = useQueryClient();
  return () => {
    (AFFECTED_KEYS[entityType] ?? []).forEach((key) =>
      queryClient.invalidateQueries({ queryKey: [key] }),
    );
    queryClient.invalidateQueries({ queryKey: ['import-batches'] });
  };
}

/**
 * Phase 1→2: hand the file over and get back the guessed mapping, a 5-row
 * preview, and the verdict on every row in the file.
 *
 * Never retried — apiUpload already opts out, and re-parsing a spreadsheet
 * because one response was slow is exactly the kind of retry that turns a
 * loaded page into a memory problem on the server.
 */
export function usePreviewImport(entityType) {
  return useMutation({
    mutationFn: ({ file, templateId, constants }) => {
      const form = new FormData();
      form.append('file', file);
      if (templateId) form.append('template_id', templateId);
      // One value for a whole file, for a field no column carries — see
      // core/import_engine.validate_rows()'s `constants`. Sent to the
      // preview as well as the commit, or the operator would be shown a
      // verdict computed without the thing they just supplied.
      if (constants && Object.keys(constants).length) {
        form.append('field_constants', JSON.stringify(constants));
      }
      return apiUpload(`/api/import/preview/${entityType}`, form);
    },
  });
}

/** Phase 3: write it. The same file goes up again — see the module docstring
 *  in blueprints/imports.py for why that beats parking it server-side. */
export function useCommitImport(entityType) {
  const invalidate = useImportInvalidation(entityType);
  return useMutation({
    mutationFn: ({ file, mapping, saveAsTemplate, templateName, constants }) => {
      const form = new FormData();
      form.append('file', file);
      form.append('final_column_mapping', JSON.stringify(mapping));
      if (constants && Object.keys(constants).length) {
        form.append('field_constants', JSON.stringify(constants));
      }
      if (saveAsTemplate) {
        form.append('save_as_template', '1');
        if (templateName) form.append('template_name', templateName);
      }
      return apiUpload(`/api/import/commit/${entityType}`, form);
    },
    onSuccess: invalidate,
  });
}

/** Recent imports, newest first — the list the undo button lives on. */
export function useImportBatches(entityType) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['import-batches', tenantId, entityType ?? 'all'],
    queryFn: () =>
      api(`/api/import/batches${entityType ? `?entity_type=${entityType}` : ''}`),
    enabled: Boolean(tenantId),
    placeholderData: [],
  });
}

/**
 * Undo one import.
 *
 * This is what makes the upsert safe to offer at all: without it, a file
 * with a bad column overwrites real contact details and there is no way
 * back. See import_batch_rows in schema.sql.
 */
export function useRevertImport(entityType) {
  const invalidate = useImportInvalidation(entityType);
  return useMutation({
    mutationFn: (batchId) =>
      api(`/api/import/batches/${batchId}/revert`, { method: 'POST', retry: false }),
    onSuccess: invalidate,
  });
}
